import {
  ACCOUNT_TYPE_TOOL,
  AUCTION_REQUEST_DECLINED_REASON_NO_CAPACITY,
  AUCTION_STATUS_PAUSED,
  DTA_MEDICAL_FORM_APPROVED_BY,
  ONBOARDING_EMAIL_PRIO,
  ONBOARDING_EMAIL_REGULAR,
  ONBOARDING_EMAIL_REMINDER,
  ORIGIN_RECEIVER,
  ORIGIN_SENDER,
  ORIGIN_TOOLS,
  PATIENT_ELIGIBILITY_APPROVED,
  PATIENT_ELIGIBILITY_IN_PROCESS,
  PATIENT_ELIGIBILITY_REJECTED,
  PATIENT_ELIGIBILITY_TO_BE_PROCESSED,
  PATIENT_FORM_ALREADY_SHARED,
  PATIENT_FORM_DRAFT,
  PATIENT_FORM_READY,
  RECARE_FILE_TYPE,
  REQUEST_UNAVAILABLE_REASON_ACCEPTED,
  REQUEST_UNAVAILABLE_REASON_STOPPED,
  REQUEST_UNAVAILABLE_REASON_SUCCESS,
  SSO_STATUS_ACTIVE,
  SSO_STATUS_INACTIVE,
} from "core/consts";
import { EVENTS } from "core/consts/events";
import { getName, getProviderSearchAccountName } from "core/model/accounts";
import {
  auctionSuccessEvent,
  declineDetailsReason,
  isHospitalSearch,
  isRehabSearch,
} from "core/model/auctions";
import { getAuctionStopReason } from "core/model/auctions/stopReasons";
import {
  getBouncedEmailsTranslations,
  getPatientUpdatedFields,
  getRejectedProviderFilterReason,
} from "core/model/requests";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { grammaticalList } from "core/model/utils/strings";
import {
  AcceptedSolution,
  Account,
  AuctionRequest,
  Event,
  EventLocation,
  EventType,
  Filev1,
  GetOntologyType,
  IconType,
  SearchType,
} from "core/types";
import {
  getAcceptedMessages,
  getLegacyAcceptedMessages,
  getMessage,
  isEncrypted,
  whoIsSender,
  whoTookAction,
} from "dsl/atoms/EventSpecs/utils";
import { ERROR_ICON } from "dsl/molecules/Messenger/EventCard";
import { getTranslateWithdrawReason } from "dsl/static/utils";
import {
  AlertOctagonIcon,
  ArchiveIcon,
  BanIcon,
  CheckIcon,
  CircleAlertIcon,
  ClipboardListIcon,
  FileInputIcon,
  FileOutputIcon,
  ListPlusIcon,
  LockIcon,
  MapPinIcon,
  MessageCircleIcon,
  MessageSquareIcon,
  PaperclipIcon,
  PauseIcon,
  PencilIcon,
  PrinterIcon,
  RefreshCwIcon,
  RotateCcwIcon,
  RotateCwIcon,
  SendIcon,
  SignatureIcon,
  StarIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  Trash2Icon,
  UndoIcon,
  UserIcon,
  XIcon,
} from "lucide-react";
import { LinkProps } from "react-router-dom";
import { Locale as LocaleString } from "translations";
import { getTranslationForAccount } from "translations/helpers";
import Translations from "translations/types";

type GetEventSpecsProps = {
  accountId?: Account["id"] | null;
  allContactedTranslation: string;
  auctionRequest?: AuctionRequest;
  auctionSuccessUnknownTranslation: string;
  currentLocale: Locale;
  event: Event;
  forIsik?: boolean;
  getOntology: GetOntologyType;
  locale: LocaleString;
  location: EventLocation;
  loggedCareproviderId: number | undefined;
  loggedCareseekerId: number | undefined;
  notNeededTranslation: string;
  patientId?: string;
  searchType: SearchType | undefined;
  sharePatientDataAutomatically?: boolean;
  translations: Translations;
};

export type EventSpecMessage = {
  date?: string;
  link?: {
    label: string;
    to: LinkProps["to"];
  };
  reasons?: string[];
  time?: string;
  title: string | null;
};

export type EventSpecs = {
  acceptedSolutionsLabel?: string;
  eventMessages?: EventSpecMessage[];
  icon?: IconType;
  message?: string | null;
  messages?: string[] | null;
  receiverTitle?: string | null | undefined;
  recieverIcon?: IconType;
  sentByLabel?: string | null | undefined;
  shouldBlur?: boolean;
  shouldHide?: EventLocation[];
  title: string;
  variant?: "error" | "success" | "warning";
};

const safeLogEvent = (event: Event) =>
  JSON.stringify(
    {
      ...event,
      context: {
        ...event.context,
        file: {
          ...event.context?.file,
          encrypted_name: {
            ...event.context?.file?.encrypted_name,
            decrypted: "N/A",
          },
        },
        message: "N/A",
      },
    },
    null,
    2,
  );

function getFileLink({
  translations,
  ...eventMessage
}: Partial<EventSpecMessage> & {
  translations: Translations;
}): EventSpecMessage {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set("activeTab", "files");

  const result: EventSpecMessage = {
    title: null,
    ...eventMessage,
    link: {
      label: translations.fileSection.seeDocument,
      to: { search: searchParams.toString() },
    },
  };

  return result;
}

const getFileNameConfig = (
  file: Filev1 | null | undefined,
  translations: Translations,
): Partial<EventSpecs> => {
  if (!file) {
    return { eventMessages: undefined };
  }

  const { encrypted_name, name, title } = file;

  // Retro compatibility w/ previous files
  if (encrypted_name == null)
    return {
      eventMessages: [getFileLink({ title: `${title} ${name}`, translations })],
    };

  if (
    !encrypted_name?.content ||
    !encrypted_name?.iv ||
    !encrypted_name?.decrypted
  )
    return {
      eventMessages: [
        getFileLink({
          title: translations.auctionRequest.encryptedMessage,
          translations,
        }),
      ],
      shouldBlur: true,
    };

  return {
    eventMessages: [
      getFileLink({ title: encrypted_name.decrypted, translations }),
    ],
  };
};

const shouldUseAcceptedSolutionEventSpecs = (
  acceptedSolutions: AcceptedSolution[] | undefined,
) => {
  const acceptedSolutionsLength = acceptedSolutions?.length ?? 0;
  const inCompleteData =
    acceptedSolutionsLength &&
    acceptedSolutions?.some(
      (acceptedSolution: AcceptedSolution) => !acceptedSolution?.solution,
    );
  if (acceptedSolutionsLength > 1 && inCompleteData) {
    throw new Error(
      `Accepted solutions returned with incomplete data ${JSON.stringify(
        acceptedSolutions,
        null,
        2,
      )}`,
    );
  }
  return acceptedSolutionsLength && !inCompleteData;
};

// back compatibility w/ previous form types
const REHAB_FORM_TYPES = {
  GENERAL: 1,
  MEDICAL: 2,
} as const;

function getFileName(event: Event, translations: Translations) {
  if (event.context?.file_type) {
    switch (event.context?.file_type) {
      case REHAB_FORM_TYPES.GENERAL:
        return translations.insuranceApp.eligibilityTab.requestFormName;
      case REHAB_FORM_TYPES.MEDICAL:
        return translations.insuranceApp.eligibilityTab.medicalFormName;
      default:
        return "";
    }
  }
  switch (event.context?.file_type_value) {
    case RECARE_FILE_TYPE.GKV_GENERAL_FORM:
    case RECARE_FILE_TYPE.DRV_GENERAL_FORM:
      return translations.insuranceApp.eligibilityTab.requestFormName;
    case RECARE_FILE_TYPE.GKV_MEDICAL_FORM:
    case RECARE_FILE_TYPE.DRV_MEDICAL_FORM:
      return translations.insuranceApp.eligibilityTab.medicalFormName;
    default:
      return "";
  }
}

function getFormEventTitle({
  accountName,
  event,
  translations,
}: {
  accountName: string;
  event: Event;
  translations: Translations;
}): string | null {
  const formType = event.context?.form_type ?? event?.context?.file_type_value;

  if (!formType) {
    return null;
  }

  switch (event.type) {
    case EVENTS.FORM_CREATED:
      switch (formType) {
        case RECARE_FILE_TYPE.GKV_GENERAL_FORM:
          return translations.patientForms.documentationTab.gkvGeneralCreated({
            name: accountName,
          });
        case RECARE_FILE_TYPE.GKV_MEDICAL_FORM:
          return translations.patientForms.documentationTab.gkvMedicalCreated({
            name: accountName,
          });
        case RECARE_FILE_TYPE.DRV_GENERAL_FORM:
          return translations.patientForms.documentationTab.drvGeneralCreated({
            name: accountName,
          });
        case RECARE_FILE_TYPE.DRV_MEDICAL_FORM:
          return translations.patientForms.documentationTab.drvMedicalCreated({
            name: accountName,
          });
        case RECARE_FILE_TYPE.TRANSITIONAL_CARE:
          return translations.timeline.transitionalCareCreated({ accountName });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_ENT:
          return translations.timeline.dischargeLetterEntCreated({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_CARDIOLOGY:
          return translations.timeline.dischargeLetterCardiologyCreated({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_TRAUMA_SURGERY:
          return translations.timeline.dischargeLetterAccidentSurgeryCreated({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_NEUROSURGERY:
          return translations.timeline.dischargeLetterNeurosurgeryCreated({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_SURGERY:
          return translations.timeline.dischargeLetterSurgeryCreated({
            accountName,
          });
        default:
          return null;
      }

    case EVENTS.FORM_UPDATED:
      switch (formType) {
        case RECARE_FILE_TYPE.GKV_GENERAL_FORM:
          return translations.patientForms.documentationTab.gkvGeneralEdited({
            name: accountName,
          });
        case RECARE_FILE_TYPE.GKV_MEDICAL_FORM:
          return translations.patientForms.documentationTab.gkvMedicalEdited({
            name: accountName,
          });
        case RECARE_FILE_TYPE.DRV_GENERAL_FORM:
          return translations.patientForms.documentationTab.drvGeneralEdited({
            name: accountName,
          });
        case RECARE_FILE_TYPE.DRV_MEDICAL_FORM:
          return translations.patientForms.documentationTab.drvMedicalEdited({
            name: accountName,
          });
        case RECARE_FILE_TYPE.TRANSITIONAL_CARE:
          return translations.timeline.transitionalCareUpdated({ accountName });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_ENT:
          return translations.timeline.dischargeLetterEntUpdated({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_CARDIOLOGY:
          return translations.timeline.dischargeLetterCardiologyUpdated({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_TRAUMA_SURGERY:
          return translations.timeline.dischargeLetterAccidentSurgeryUpdated({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_NEUROSURGERY:
          return translations.timeline.dischargeLetterNeurosurgeryUpdated({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_SURGERY:
          return translations.timeline.dischargeLetterEdited({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_OBSTETRICS:
          return translations.timeline.dischargeLetterObstetricsEdited({
            accountName,
          });
        default:
          return null;
      }

    case EVENTS.FORM_DELETED:
      switch (formType) {
        case RECARE_FILE_TYPE.GKV_GENERAL_FORM:
          return translations.patientForms.documentationTab.gkvGeneralDeleted({
            name: accountName,
          });
        case RECARE_FILE_TYPE.GKV_MEDICAL_FORM:
          return translations.patientForms.documentationTab.gkvMedicalDeleted({
            name: accountName,
          });
        case RECARE_FILE_TYPE.DRV_GENERAL_FORM:
          return translations.patientForms.documentationTab.drvGeneralDeleted({
            name: accountName,
          });
        case RECARE_FILE_TYPE.DRV_MEDICAL_FORM:
          return translations.patientForms.documentationTab.drvMedicalDeleted({
            name: accountName,
          });
        case RECARE_FILE_TYPE.TRANSITIONAL_CARE:
          return translations.timeline.transitionalCareDeleted({ accountName });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_ENT:
          return translations.timeline.dischargeLetterEntDeleted({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_CARDIOLOGY:
          return translations.timeline.dischargeLetterCardiologyDeleted({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_TRAUMA_SURGERY:
          return translations.timeline.dischargeLetterAccidentSurgeryDeleted({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_NEUROSURGERY:
          return translations.timeline.dischargeLetterNeurosurgeryDeleted({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_SURGERY:
          return translations.timeline.dischargeLetterSurgeryDeleted({
            accountName,
          });
        case RECARE_FILE_TYPE.DISCHARGE_LETTER_OBSTETRICS:
          return translations.timeline.dischargeLetterObstetricsDeleted({
            accountName,
          });
        default:
          return null;
      }

    default:
      return null;
  }
}

function getOnboardingEmailType(event: Event, translations: Translations) {
  switch (event?.context?.onboarding_email_type) {
    case ONBOARDING_EMAIL_REGULAR:
      return translations.timeline.onboardingEmail.regular;
    case ONBOARDING_EMAIL_PRIO:
      return translations.timeline.onboardingEmail.prio;
    case ONBOARDING_EMAIL_REMINDER:
      return translations.timeline.onboardingEmail.reminder;
    default:
      return translations.timeline.onboardingEmail.notSpecified;
  }
}

function getPatientWishReasonMessage(event: Event, translations: Translations) {
  if (!event.context?.message)
    return {
      title:
        translations.timeline.addedPatientWishReason.deletedPatientWishReason,
    };

  return {
    title: event.context?.message,
  };
}

function getRequestAcceptedSpecs({
  encrypted,
  event,
  getOntology,
  locale,
  modularAccountName,
  providerName,
  translations,
}: Pick<
  GetEventSpecsProps,
  "event" | "getOntology" | "locale" | "translations"
> & {
  encrypted: boolean;
  modularAccountName: string;
  providerName: string;
}): EventSpecs {
  const acceptedEventProps: Partial<EventSpecs> = {
    icon: CheckIcon,
    recieverIcon: CheckIcon,
    variant: "success",
  };
  const acceptedSolutions = event.context?.accepted_solutions;
  if (shouldUseAcceptedSolutionEventSpecs(acceptedSolutions)) {
    return {
      ...acceptedEventProps,
      acceptedSolutionsLabel: grammaticalList({
        array: event.context?.accepted_solutions?.map(
          ({ solution }) => solution,
        ),
        getOntology,
        ontologyType: "solution",
        translations,
      }),
      receiverTitle:
        translations.responseEvents.acceptedMultiSolution.myAccount,
      title: translations.responseEvents.acceptedMultiSolution.otherAccount({
        providerName,
      }),
      ...getAcceptedMessages({
        encrypted,
        event,
        getOntology,
        locale,
        translations,
      }),
    };
  }
  // LEGACY EVENT CONTEXT
  return {
    ...acceptedEventProps,
    receiverTitle: getTranslationForAccount(
      translations.responseEvents.acceptedTheRequest,
      modularAccountName,
    ),
    title: translations.requestEvents.providerAcceptedTheRequest({
      providerName,
    }),
    ...getLegacyAcceptedMessages({
      encrypted,
      event,
      locale,
      translations,
    }),
  };
}

function getRequestUnavailableOrRejected({
  encrypted,
  event,
  getOntology,
  location,
  modularAccountName,
  notNeededTranslation,
  providerName,
  translations,
  type,
}: Pick<
  GetEventSpecsProps,
  "event" | "getOntology" | "location" | "translations"
> & {
  encrypted: boolean;
  modularAccountName: string;
  notNeededTranslation: string;
  providerName: string;
  type: Event["type"];
}): EventSpecs {
  const partial: Partial<EventSpecs> = {
    icon: type === EVENTS.REQUEST_UNAVAILABLE ? BanIcon : ERROR_ICON,
    receiverTitle: translations.timeline.requestUnavailableProvider,
    variant: "error",
  };

  if (event?.context?.solution_filter_reasons) {
    const filterReasons = Object.values(event.context.solution_filter_reasons)
      .flatMap((filters) => {
        return getRejectedProviderFilterReason(
          filters,
          translations,
          getOntology,
        );
      })
      .truthy();

    const deduplicated = filterReasons.filter(
      (warningMessage: string, i: number) =>
        filterReasons.indexOf(warningMessage) === i,
    );

    return {
      ...partial,
      shouldHide: ["documentation"],
      title: `${
        translations.requestEvents.updatedPatientRuledOut
      } ${grammaticalList({ array: deduplicated, translations })}`,
    };
  } else if (event?.context?.filters) {
    const ruledOutReasons = event.context?.filters
      ? getRejectedProviderFilterReason(
          event.context.filters,
          translations,
          getOntology,
        )
      : "";

    return {
      ...partial,
      shouldHide: ["documentation"],
      title: `${translations.requestEvents.updatedPatientRuledOut} ${ruledOutReasons}`,
    };
  }

  if (
    event.context?.request_unavailable_reason ==
    REQUEST_UNAVAILABLE_REASON_SUCCESS
  ) {
    return {
      ...partial,
      shouldHide: ["documentation"],
      title: translations.timeline.anotherProviderSelected,
    };
  }
  if (
    location !== "documentation" &&
    event.type === EVENTS.RESPONSE_REJECTED &&
    event.context?.request_unavailable_reason ==
      REQUEST_UNAVAILABLE_REASON_STOPPED
  ) {
    return {
      ...partial,
      title:
        getAuctionStopReason(
          event,
          translations,
          modularAccountName,
          notNeededTranslation,
        ) || NO_TITLE,
    };
  }

  if (location === "documentation" && event.type === EVENTS.RESPONSE_REJECTED) {
    const partialTitle = getTranslationForAccount(
      translations.requestEvents.rejectedThisProvider,
      modularAccountName,
      providerName,
    );

    const internalRemarks = event?.context?.clinic_internal_remarks
      ? `${translations.general.colon} ${event.context.clinic_internal_remarks}`
      : "";

    return {
      ...partial,
      message: getMessage(event, encrypted, translations),
      receiverTitle: translations.timeline.requestUnavailableProvider,
      shouldHide: event.context?.filters ? ["documentation"] : undefined,
      title: `${partialTitle}${internalRemarks}`,
    };
  }
  if (
    event.type === EVENTS.REQUEST_UNAVAILABLE &&
    event?.context?.request_unavailable_reason ==
      REQUEST_UNAVAILABLE_REASON_ACCEPTED
  ) {
    return {
      ...partial,
      receiverTitle:
        translations.auctionResponse.requestReservedAnotherProvider,
      title: translations.auctionResponse.requestReservedAnotherProvider,
    };
  }

  return {
    ...partial,
    message: getMessage(event, encrypted, translations),
    receiverTitle: translations.timeline.requestUnavailableProvider,
    title: getTranslationForAccount(
      translations.requestEvents.rejectedThisProvider,
      modularAccountName,
      providerName,
    ),
  };
}

/**
 * a) sender shares a file
 * 2 file_shared created
 *  for origin 5 careseeker Obj careprovider null
 *  for origin 5 careseeker null careprovider Obj
 * aa) if you are careseeker you don't want to see
 *  for origin 5 careseeker Obj careprovider null (you sharing with yourself) [loggedCareseeker && origin 5 && careseekerObj]
 * ab) if you are careprovider you don't want to see
 *  for origin 5 careseeker Obj careprovider null (them sharing with themselves) [loggedCareprovider && origin 5 && careseekerObj]
 *
 *
 * b) receiver shares a file
 * 2 file_shared created
 *  for origin 2 careseeker null careprovider Obj
 *  for origin 2 careseeker Obj careprovider null
 * ba) if you are careseeker you don't want to see
 *  for origin 2 careseeker null careprovider Obj (them sharing with themselves) [loggedCareseeker && origin 2 && careproviderObj]
 * bb) if you are careprovider you don't want to see
 *  for origin 2 careseeker null careprovider Obj (you sharing with yourself) [loggedCareprovider && origin 2 && careproviderObj]
 *
 */
function dismissSealdAccessEvent({
  event,
  loggedCareproviderId,
  loggedCareseekerId,
}: {
  event: Event;
  loggedCareproviderId: number | undefined;
  loggedCareseekerId: number | undefined;
}) {
  return !!(
    (loggedCareseekerId &&
      event.origin === ORIGIN_SENDER &&
      event.careseeker?.id) ||
    (loggedCareproviderId &&
      event.origin === ORIGIN_SENDER &&
      event.careseeker?.id) ||
    (loggedCareseekerId &&
      event.origin === ORIGIN_RECEIVER &&
      event.careprovider?.id) ||
    (loggedCareproviderId &&
      event.origin === ORIGIN_RECEIVER &&
      event.careprovider?.id)
  );
}

const NO_TITLE = "NO_TITLE" as const;
const NO_EVENT = "NO EVENT" as const;

export function getEventSpecs({
  accountId,
  allContactedTranslation,
  auctionSuccessUnknownTranslation,
  currentLocale,
  event,
  forIsik,
  getOntology,
  locale,
  location,
  loggedCareproviderId,
  loggedCareseekerId,
  notNeededTranslation,
  searchType,
  sharePatientDataAutomatically,
  translations,
}: GetEventSpecsProps): EventSpecs | undefined {
  const accountName = getName(event.account, getOntology, {
    providerSearchAccountName: getProviderSearchAccountName({
      patientId: event?.context?.user_id,
      translations,
    }),
  });
  const encrypted = isEncrypted(event);
  const translateWithdrawReason = getTranslateWithdrawReason(translations);
  const providerName =
    event.careprovider?.name ?? event?.context?.careprovider_name ?? "";
  const newAssigneeName = event?.context?.new_assignee_name ?? "";
  const oldAssigneeName = event?.context?.old_assignee_name ?? "";
  const modularAccountName =
    whoTookAction(accountId, event, accountName, !!forIsik) ?? "";
  const isInternalToolAccount =
    event?.account?.account_type === ACCOUNT_TYPE_TOOL ||
    event?.origin === ORIGIN_TOOLS;

  function getSpecs(type: EventType): EventSpecs | typeof NO_EVENT | undefined {
    switch (type) {
      case EVENTS.MAIL_FAILURE: {
        return {
          title: translations.timeline.mailFailure,
          ...(event.context?.message && {
            eventMessages: [{ title: event.context.message }],
          }),
        };
      }

      case EVENTS.REQUEST_AVAILABLE: {
        return {
          icon: RotateCwIcon,
          title: translations.timeline.requestAvailable,
        };
      }

      case EVENTS.AUCTION_CANCELLED: {
        return {
          icon: ERROR_ICON,
          title:
            getAuctionStopReason(
              event,
              translations,
              modularAccountName,
              notNeededTranslation,
            ) || NO_TITLE,
          variant: "error",
        };
      }

      case EVENTS.VALIDATION_CANCELLED: {
        let title = "";
        let receiverTitle = null;
        if (event.context?.reason != null) {
          const reason = event.context?.reason || "";
          receiverTitle =
            translations.responseEvents.clinicCanceledAttributionWithReason({
              accountName,
              reason,
            });
          if (providerName) {
            title = translations.timeline.cancelledValidation({
              providerName,
              reason,
              workerName: accountName,
            });
          } else {
            title =
              translations.timeline.auctionValidationCanceledUnknownReason({
                accountName,
                reason,
              });
          }
        } else {
          receiverTitle = translations.responseEvents.clinicCanceledAttribution(
            { accountName },
          );
          if (providerName) {
            title = getTranslationForAccount(
              translations.requestEvents.canceledProviderValidation,
              modularAccountName,
              providerName,
            );
          } else {
            title = translations.timeline.auctionValidationCanceledUnknown({
              accountName,
            });
          }
        }

        return {
          icon: UndoIcon,
          receiverTitle,
          title,
        };
      }

      case EVENTS.REQUEST_TO_BE_SENT: {
        return {
          icon: ListPlusIcon,
          title: translations.timeline.requestToBeSent({
            accountName,
            careprovider: providerName,
          }),
        };
      }

      case EVENTS.SUGGESTION_CANCELLED: {
        return {
          icon: BanIcon,
          title: translations.timeline.suggestionCancelled({
            accountName,
            careprovider: providerName,
          }),
        };
      }

      case EVENTS.REFUSAL_CANCELLED: {
        return {
          icon: UndoIcon,
          title: getTranslationForAccount(
            translations.requestEvents.canceledProviderRejection,
            modularAccountName,
            providerName,
          ),
        };
      }

      case EVENTS.AUCTION_ALL_CONTACTED: {
        return {
          icon: AlertOctagonIcon,
          title: allContactedTranslation,
        };
      }

      case EVENTS.PATIENT_DISCHARGED: {
        return {
          title: translations.timeline.patientDischarged,
        };
      }

      case EVENTS.PATIENT_CREATED: {
        return {
          title: translations.timeline.patientCreated,
        };
      }

      case EVENTS.SYSTEM_ERROR: {
        return {
          title: "System error",
          ...(event.context?.message && {
            eventMessages: [{ title: event.context.message }],
          }),
        };
      }

      case EVENTS.REQUEST_CREATED: {
        return {
          icon: ListPlusIcon,
          title: getTranslationForAccount(
            translations.requestEvents.addedTheProvider,
            modularAccountName,
            providerName,
          ),
        };
      }

      case EVENTS.FAX_DELIVERED: {
        return {
          icon: PrinterIcon,
          title: translations.faxRequest.faxEvents.faxDelivered({
            receivername: providerName,
          }),
        };
      }

      case EVENTS.FAX_FAILED: {
        return {
          icon: CircleAlertIcon,
          title: translations.faxRequest.faxEvents.faxBounced,
        };
      }

      case EVENTS.FAX_SENT: {
        return {
          icon: SendIcon,
          title: translations.faxRequest.faxEvents.faxSent({
            receivername: providerName,
          }),
        };
      }

      case EVENTS.FAX_NOT_AVAILABLE: {
        return {
          icon: BanIcon,
          title: translations.faxRequest.faxEvents.faxNumberNotAvailable({
            receivername: providerName,
          }),
        };
      }

      case EVENTS.CAREPROVIDER_ACCEPT_CANCELLED: {
        return {
          icon: UndoIcon,
          receiverTitle: getTranslationForAccount(
            translations.responseEvents.canceledAcceptance,
            modularAccountName,
          ),
          title: translations.requestEvents.canceledAcceptance({
            providerName,
          }),
        };
      }

      case EVENTS.CAREPROVIDER_DECLINE_CANCELLED: {
        return {
          icon: UndoIcon,
          receiverTitle: getTranslationForAccount(
            translations.responseEvents.canceledDeclination,
            modularAccountName,
          ),
          title: translations.requestEvents.canceledDeclination({
            providerName,
          }),
          ...(event.context?.declined_reason ===
            AUCTION_REQUEST_DECLINED_REASON_NO_CAPACITY && {
            eventMessages: [
              {
                title:
                  translations.auctionResponse.noCapacityReasons
                    .reasonCancelDeclination,
              },
            ],
          }),
        };
      }

      case EVENTS.REQUEST_MESSAGE_TO_CLINIC:
      case EVENTS.ENCRYPTED_MESSAGE:
      case EVENTS.ELIGIBILITY_MESSAGE:
      case EVENTS.REQUEST_MESSAGE_TO_PROVIDER: {
        return {
          icon: MessageCircleIcon,
          message: getMessage(event, encrypted, translations),
          receiverTitle: getTranslationForAccount(
            translations.requestEvents.wrote,
            modularAccountName,
            providerName,
          ),
          title: getTranslationForAccount(
            translations.requestEvents.wrote,
            modularAccountName,
            providerName,
          ),
        };
      }

      case EVENTS.REQUEST_DECLINED: {
        const message = declineDetailsReason({
          getOntology,
          locale: currentLocale,
          options: event.context,
          searchType: event.context?.patient_type,
          translations,
        });

        const [eventMessageTitle, ...reasons] = message?.split("\n") || "";

        return {
          receiverTitle: getTranslationForAccount(
            translations.responseEvents.declinedRequest,
            modularAccountName,
          ),
          title: translations.requestEvents.providerDeclinedRequest({
            providerName,
          }),
          ...(eventMessageTitle && {
            eventMessages: [{ reasons, title: eventMessageTitle }],
          }),
          icon: XIcon,
          variant: "error",
        };
      }

      case EVENTS.REQUEST_ACCEPTED: {
        return getRequestAcceptedSpecs({
          encrypted,
          event,
          getOntology,
          locale,
          modularAccountName,
          providerName,
          translations,
        });
      }

      case EVENTS.REQUEST_SENT: {
        // If accountName === "" (internal tool), then display careseeker's name.
        const socialWorker = accountName || event?.careseeker?.name || "";

        if (event.context?.manual) {
          return {
            icon: SendIcon,
            message: getMessage(event, encrypted, translations),
            title: translations.timeline.requestManuallyAdded({
              careprovider: providerName,
              socialWorker,
            }),
          };
        }

        return {
          icon: SendIcon,
          receiverTitle: translations.responseEvents.requestReceived({
            socialWorker,
          }),
          shouldHide: ["documentation"],
          title: translations.requestEvents.requestSent({
            careprovider: providerName,
          }),
        };
      }

      case EVENTS.PATIENT_REQUEST_UPDATED: {
        const message = getPatientUpdatedFields({
          diffs: event.context?.diff,
          translations,
        });
        const [eventMessageTitle, ...reasons] = message?.split("\n") || "";

        return {
          title: getTranslationForAccount(
            translations.requestEvents.updatedPatient,
            modularAccountName,
          ),
          ...(eventMessageTitle && {
            eventMessages: [{ reasons, title: eventMessageTitle }],
          }),
          icon: PencilIcon,
        };
      }

      case EVENTS.PATIENT_UPDATED:
      case EVENTS.AUCTION_UPDATED: {
        const message = getPatientUpdatedFields({
          diffs: event.context?.diff,
          translations,
        });

        const [eventMessageTitle, ...reasons] = message?.split("\n") || "";

        const infoExcludedProviderKey = eventMessageTitle
          ? translations.timeline.patientUpdated.infoExcludedProviders({
              reason: eventMessageTitle,
            })
          : translations.timeline.patientUpdated.infoExcludedProvidersNoReason;

        return {
          title: `${getTranslationForAccount(
            translations.timeline.patientUpdated,
            modularAccountName,
          )} ${infoExcludedProviderKey}`,
          ...(eventMessageTitle && {
            eventMessages: [{ reasons, title: eventMessageTitle }],
          }),
          icon: PencilIcon,
        };
      }

      case EVENTS.REQUEST_STATUS_CHANGE: {
        return NO_EVENT;
      }

      case EVENTS.AUCTION_TO_BE_STARTED: {
        return {
          icon: SendIcon,
          title: translations.timeline.auctionToBeStarted({
            accountName,
          }),
        };
      }

      case EVENTS.DISMISSAL_CANCELLED: {
        return {
          icon: UndoIcon,
          title: translations.timeline.cancelRejection({
            accountName,
            careprovider: providerName,
          }),
        };
      }

      case EVENTS.AUCTION_PAUSED: {
        return {
          icon: PauseIcon,
          title: isInternalToolAccount
            ? translations.timeline.auctionPaused.automatic
            : getTranslationForAccount(
                translations.timeline.auctionPaused,
                modularAccountName,
              ),
        };
      }

      case EVENTS.AUCTION_STATUS_CHANGED: {
        const type = event.context?.new_status;
        switch (type) {
          case AUCTION_STATUS_PAUSED:
            return {
              icon: PauseIcon,
              title: isInternalToolAccount
                ? translations.timeline.auctionPaused.automatic
                : getTranslationForAccount(
                    translations.timeline.auctionPaused,
                    modularAccountName,
                  ),
            };
          default:
            return NO_EVENT;
        }
      }

      case EVENTS.USER_PASSWORD_CHANGED: {
        return {
          title: translations.acp.accountTimeline.passwordChange,
        };
      }

      case EVENTS.PATIENT_AUTOMATICALLY_ARCHIVED: {
        return {
          icon: ArchiveIcon,
          title: translations.responseEvents.searchArchivedAutomatically,
        };
      }

      case EVENTS.AUCTION_STARTED: {
        return {
          title: translations.timeline.auctionStarted({
            accountName,
          }),
        };
      }

      case EVENTS.AUCTION_SUCCESS: {
        const trasnferredToSpecialization =
          event.context?.transferred_to_specialization;
        if (trasnferredToSpecialization) {
          const specialization = getOntology({
            key: trasnferredToSpecialization,
            type: ONTOLOGIES.SPECIALIZATIONS,
          });
          return {
            title: `${translations.patient.stopAuction.internalTransfer} - ${specialization}`,
          };
        }
        return {
          title: auctionSuccessEvent(
            event,
            translations,
            getOntology,
            auctionSuccessUnknownTranslation,
          ),
        };
      }

      case EVENTS.FILE_UPLOAD:
      case EVENTS.ELIGIBILITY_FILE_UPLOAD: {
        return {
          icon: PaperclipIcon,
          receiverTitle: getTranslationForAccount(
            translations.requestEvents.sentaFile,
            modularAccountName,
          ),
          title: getTranslationForAccount(
            translations.requestEvents.sentaFile,
            modularAccountName,
          ),
          ...getFileNameConfig(event?.context?.file, translations),
        };
      }

      case EVENTS.FILE_SHARED: {
        if (
          dismissSealdAccessEvent({
            event,
            loggedCareproviderId,
            loggedCareseekerId,
          })
        ) {
          return NO_EVENT;
        }
        if (modularAccountName) {
          return {
            eventMessages: [getFileLink({ translations })],
            icon: PaperclipIcon,
            title: translations.timeline.filesChanged.fileSharedOtherAccount({
              accountName: modularAccountName,
            }),
          };
        }

        const recipientName =
          event.careprovider?.name ?? event.careseeker?.name;
        if (recipientName) {
          return {
            eventMessages: [getFileLink({ translations })],
            icon: PaperclipIcon,
            title: translations.timeline.filesChanged.fileSharedMyAccount({
              name: recipientName,
            }),
          };
        }

        return {
          eventMessages: [getFileLink({ translations })],
          icon: PaperclipIcon,
          title: getTranslationForAccount(
            translations.requestEvents.sentaFile,
            modularAccountName,
          ),
        };
      }

      case EVENTS.FILE_UPLOADED: {
        if (modularAccountName) return NO_EVENT;
        return {
          icon: PaperclipIcon,
          title: translations.timeline.filesChanged.fileUploaded,
        };
      }

      case EVENTS.FILE_SHARE_MODE_CHANGED: {
        if (modularAccountName)
          return {
            icon: PaperclipIcon,
            title:
              translations.timeline.filesChanged.fileAccessChangedOtherAccount({
                accountName: modularAccountName,
              }),
          };
        return {
          icon: PaperclipIcon,
          title: translations.timeline.filesChanged.fileAccessChangedMyAccount({
            newFileShareMode: getOntology({
              key: event.context?.new_status,
              type: "fileShareMode",
            }),
            previousFileShareMode: getOntology({
              key: event.context?.previous_status,
              type: "fileShareMode",
            }),
          }),
        };
      }

      case EVENTS.FILE_ACCESS_REVOKED: {
        if (
          dismissSealdAccessEvent({
            event,
            loggedCareproviderId,
            loggedCareseekerId,
          })
        ) {
          return NO_EVENT;
        }
        if (modularAccountName)
          return {
            icon: PaperclipIcon,
            title:
              translations.timeline.filesChanged.fileAccessRevokedOtherAccount({
                name: modularAccountName,
              }),
          };
        const recipientName =
          event.careprovider?.name ?? event.careseeker?.name;
        if (recipientName)
          return {
            icon: PaperclipIcon,
            title:
              translations.timeline.filesChanged.fileAccessRevokedMyAccount({
                name: recipientName,
              }),
          };
        return {
          icon: PaperclipIcon,
          title:
            translations.timeline.filesChanged
              .fileAccessRevokedMyAccountMessenger,
        };
      }

      case EVENTS.FILE_DELETED: {
        const formType =
          event.context?.form_type ?? event.context?.file_type_value;
        switch (formType) {
          case RECARE_FILE_TYPE.GKV_GENERAL_FORM:
            return {
              icon: Trash2Icon,
              title:
                translations.patientForms.documentationTab.gkvGeneralDeleted({
                  name: accountName,
                }),
            };
          case RECARE_FILE_TYPE.GKV_MEDICAL_FORM:
            return {
              icon: Trash2Icon,
              title:
                translations.patientForms.documentationTab.gkvMedicalDeleted({
                  name: accountName,
                }),
            };
          case RECARE_FILE_TYPE.DRV_GENERAL_FORM:
            return {
              icon: Trash2Icon,
              title:
                translations.patientForms.documentationTab.drvGeneralDeleted({
                  name: accountName,
                }),
            };
          case RECARE_FILE_TYPE.DRV_MEDICAL_FORM:
            return {
              icon: Trash2Icon,
              title:
                translations.patientForms.documentationTab.drvMedicalDeleted({
                  name: accountName,
                }),
            };
          default:
            if (modularAccountName)
              return {
                icon: Trash2Icon,
                title:
                  translations.timeline.filesChanged.fileDeletedOtherAccount({
                    name: modularAccountName,
                  }),
              };
            return {
              icon: Trash2Icon,
              title: translations.timeline.filesChanged.fileDeletedMyAccount,
            };
        }
      }

      case EVENTS.RESPONSE_APPROVED: {
        const message = getMessage(event, encrypted, translations);
        return {
          icon: CheckIcon,
          receiverTitle: translations.responseEvents.patientAttributedToYou,
          title: getTranslationForAccount(
            translations.requestEvents.validatedThisProvider,
            modularAccountName,
            providerName,
          ),
          variant: "success",
          ...(message && { messages: [message] }),
          ...(event.context?.winning_solution && {
            eventMessages: [
              {
                title: getOntology({
                  key: event.context?.winning_solution,
                  type: ONTOLOGIES.SOLUTION,
                }),
              },
            ],
          }),
        };
      }

      case EVENTS.REQUEST_UNAVAILABLE:
      case EVENTS.RESPONSE_REJECTED: {
        return getRequestUnavailableOrRejected({
          encrypted,
          event,
          getOntology,
          location,
          modularAccountName,
          notNeededTranslation,
          providerName,
          translations,
          type: event.type,
        });
      }

      case EVENTS.REQUEST_DISMISSED: {
        return {
          icon: BanIcon,
          message: getMessage(event, encrypted, translations),
          title: translations.timeline.requestDismissed({
            accountName,
            careprovider: providerName,
          }),
          variant: "error",
        };
      }

      case EVENTS.MAIL_BOUNCED: {
        return {
          icon: CircleAlertIcon,
          title: getBouncedEmailsTranslations(event, translations),
        };
      }

      case EVENTS.REQUEST_AUTOMATICALLY_ARCHIVED: {
        return {
          icon: ArchiveIcon,
          title: translations.responseEvents.requestArchivedAutomatically,
        };
      }

      case EVENTS.REQUEST_AUTOMATICALLY_UNARCHIVED: {
        return {
          icon: UndoIcon,
          title: translations.responseEvents.requestArchivedManually.undo.tools,
        };
      }

      case EVENTS.REQUEST_MANUALLY_ARCHIVED: {
        return {
          icon: ArchiveIcon,
          title: isInternalToolAccount
            ? translations.responseEvents.requestArchivedManually.tools
            : getTranslationForAccount(
                translations.responseEvents.requestArchivedManually,
                modularAccountName,
              ),
        };
      }

      case EVENTS.REQUEST_MANUALLY_UNARCHIVED: {
        return {
          icon: UndoIcon,
          title: isInternalToolAccount
            ? translations.responseEvents.requestArchivedManually.undo.tools
            : getTranslationForAccount(
                translations.responseEvents.requestArchivedManually.undo,
                modularAccountName,
              ),
        };
      }

      case EVENTS.SHARE_PATIENT_INFORMATION: {
        return {
          icon: LockIcon,
          title: sharePatientDataAutomatically // still need to keep this for a while to backfill old events, until patients are old enough(redacted)
            ? translations.requestEvents.sharedPatient.automatic({
                providerName,
              })
            : getTranslationForAccount(
                translations.requestEvents.sharedPatient,
                modularAccountName,
                providerName,
              ),
        };
      }

      case EVENTS.SHARE_PATIENT_INFORMATION_AUTOMATICALLY: {
        return {
          icon: LockIcon,
          title:
            isHospitalSearch(searchType) || isRehabSearch(searchType)
              ? translations.timeline.partialAccessPatientData({
                  provider: providerName,
                })
              : translations.requestEvents.sharedPatient.automatic({
                  providerName,
                }),
        };
      }

      case EVENTS.ACCOUNT_ACTIVATION_EMAIL: {
        return {
          title: translations.acp.accountTimeline.activationEmailSent,
          ...(event?.context?.email && {
            eventMessages: [{ title: event.context.email }],
          }),
        };
      }

      case EVENTS.ACCOUNT_LOGIN: {
        return {
          title: translations.acp.accountTimeline.login,
        };
      }

      case EVENTS.ACCOUNT_CREATED: {
        return {
          title: translations.acp.accountTimeline.accountCreated,
        };
      }

      case EVENTS.ADMIN_SET_PASSWORD: {
        return {
          title: translations.acp.accountTimeline.passwordSetByAdmin,
        };
      }

      case EVENTS.PASSWORD_RESET_SUCCESS: {
        return {
          title: translations.acp.accountTimeline.passwordResetSuccessfully,
        };
      }

      case EVENTS.ADMIN_RESET_PASSWORD: {
        return {
          title: translations.acp.accountTimeline.resetPasswordRequestedByAdmin,
        };
      }

      case EVENTS.ACCOUNT_UPDATED: {
        return {
          title: translations.acp.accountTimeline.accountUpdated,
          ...(event?.context?.diff && {
            eventMessages: [{ title: event?.context?.diff?.join(", ") }],
          }),
        };
      }

      case EVENTS.USER_PASSWORD_RESET_EMAIL: {
        return {
          title: translations.acp.accountTimeline.resetPasswordRequestedByUser,
        };
      }

      case EVENTS.EMAIL_INVITATION: {
        return {
          title: translations.acp.invitationEmailSent({
            account: getName(event.subject_account, getOntology),
          }),
        };
      }

      case EVENTS.SENT_TO_CALLCENTER: {
        return {
          title: translations.timeline.pushedToCallcenter,
        };
      }

      case EVENTS.ONBOARDING_EMAIL_SENT: {
        return {
          eventMessages: [
            { title: getOnboardingEmailType(event, translations) },
          ],
          title: translations.timeline.onboardingEmailSent,
        };
      }

      case EVENTS.SMS_SENT: {
        return {
          icon: MessageSquareIcon,
          title: translations.timeline.smsSent,
        };
      }

      case EVENTS.CARESEEKER_UPDATED: {
        return {
          title: translations.timeline.senderUpdated,
          ...(event?.context?.diff && {
            eventMessages: [{ title: event?.context?.diff?.join(", ") }],
          }),
        };
      }

      case EVENTS.CARESEEKER_CREATED: {
        return {
          title: translations.timeline.senderCreated,
        };
      }

      case EVENTS.CAREPROVIDER_UPDATED: {
        return {
          title: translations.timeline.careproviderUpdated,
          ...(event?.context?.diff && {
            eventMessages: [{ title: event?.context?.diff?.join(", ") }],
          }),
        };
      }

      case EVENTS.CAPACITY_UPDATED: {
        return {
          title: event?.context?.capacity_confirmed
            ? translations.timeline.capacityConfirmed
            : translations.timeline.capacityUpdated,
        };
      }

      case EVENTS.CAREPROVIDER_CREATED: {
        return {
          title: translations.timeline.careproviderCreated,
        };
      }

      case EVENTS.CAREPROVIDER_TEST_SENT: {
        return {
          title: translations.timeline.testSent,
        };
      }

      case EVENTS.PATIENT_ARCHIVED: {
        return {
          icon: ArchiveIcon,
          title: translations.timeline.patientArchived,
        };
      }

      case EVENTS.CAREPROVIDER_TEST_ANSWERED: {
        return {
          title: translations.timeline.testAnswered,
        };
      }

      case EVENTS.REHAB_FORM_CREATED: {
        return {
          icon: ClipboardListIcon,
          title: translations.timeline.rehaFormCreated({
            accountName,
            formName: getFileName(event, translations),
          }),
        };
      }

      case EVENTS.REHAB_FORM_UPDATED: {
        return {
          icon: PencilIcon,
          title: translations.timeline.rehaFormUpdated({
            accountName,
            formName: getFileName(event, translations),
          }),
        };
      }

      case EVENTS.REHAB_FORM_DELETED: {
        return {
          icon: Trash2Icon,
          title: translations.timeline.rehaFormDeleted({
            accountName,
            formName: getFileName(event, translations),
          }),
        };
      }

      case EVENTS.TRANSITIONAL_CARE_FORM_CREATED: {
        return {
          icon: ClipboardListIcon,
          title: translations.timeline.transitionalCareCreated({
            accountName,
          }),
        };
      }

      case EVENTS.TRANSITIONAL_CARE_FORM_UPDATED: {
        return {
          icon: PencilIcon,
          title: translations.timeline.transitionalCareUpdated({
            accountName,
          }),
        };
      }

      case EVENTS.TRANSITIONAL_CARE_FORM_DELETED: {
        return {
          icon: Trash2Icon,
          title: translations.timeline.transitionalCareDeleted({
            accountName,
          }),
        };
      }

      case EVENTS.FORM_CREATED: {
        const title = getFormEventTitle({
          accountName,
          event,
          translations,
        });
        if (!title) return NO_EVENT;
        return { icon: ClipboardListIcon, title };
      }

      case EVENTS.FORM_UPDATED: {
        const title = getFormEventTitle({
          accountName,
          event,
          translations,
        });
        if (!title) return NO_EVENT;
        return { icon: PencilIcon, title };
      }

      case EVENTS.FORM_DELETED: {
        const title = getFormEventTitle({
          accountName,
          event,
          translations,
        });
        if (!title) return NO_EVENT;
        return { icon: Trash2Icon, title };
      }

      case EVENTS.REHAB_FORM_STATUS_CHANGED: {
        switch (event.context?.new_status) {
          case PATIENT_FORM_DRAFT: {
            return {
              icon: UndoIcon,
              title: translations.timeline.rehaFormUpdatedBackToDraft({
                accountName,
                formName: getFileName(event, translations),
              }),
            };
          }

          case PATIENT_FORM_READY: {
            return {
              icon: CheckIcon,
              title: translations.timeline.rehaFormUpdatedCompleted({
                accountName,
                formName: getFileName(event, translations),
              }),
            };
          }

          case PATIENT_FORM_ALREADY_SHARED: {
            return {
              icon: ClipboardListIcon,
              message: event.context?.message,
              title: translations.timeline.rehaFormUpdatedAlreadySent({
                accountName,
                formName: getFileName(event, translations),
              }),
            };
          }
        }
        break;
      }

      case EVENTS.ELIGIBILITY_STATUS_CHANGED: {
        switch (event.context?.new_status) {
          case PATIENT_ELIGIBILITY_TO_BE_PROCESSED:
            return {
              icon: UndoIcon,
              title: translations.timeline.eligibilityBackToNotProcessed({
                accountName,
              }),
            };
          case PATIENT_ELIGIBILITY_APPROVED: {
            return {
              icon: CheckIcon,
              title: translations.timeline.eligibilityApproved({
                accountName,
              }),
            };
          }

          case PATIENT_ELIGIBILITY_IN_PROCESS: {
            switch (event.context?.previous_status) {
              case PATIENT_ELIGIBILITY_TO_BE_PROCESSED:
                return {
                  icon: SendIcon,
                  title: translations.timeline.eligibilityInProcess({
                    accountName,
                  }),
                };
              default:
                return {
                  icon: UndoIcon,
                  message: getMessage(event, encrypted, translations),
                  title: translations.timeline.eligibilityCancelled({
                    accountName,
                  }),
                };
            }
          }

          case PATIENT_ELIGIBILITY_REJECTED: {
            const rejectReason = getMessage(event, encrypted, translations);
            return {
              icon: XIcon,
              message: `${getOntology({
                key: event.context?.eligibility_reject_reason,
                type: ONTOLOGIES.ELIGIBILITY_REJECT_REASON,
              })}${
                rejectReason
                  ? `${translations.general.colon} ${getMessage(
                      event,
                      encrypted,
                      translations,
                    )}`
                  : ""
              }`,
              title: translations.timeline.eligibilityRejected({
                accountName,
              }),
            };
          }
        }
        break;
      }

      case EVENTS.COMPANY_CREATED: {
        return {
          title: translations.timeline.companyCreated,
        };
      }

      case EVENTS.COMPANY_UPDATED: {
        return {
          title: translations.timeline.companyUpdated,
          ...(event?.context?.diff && {
            eventMessages: [{ title: event?.context?.diff?.join(", ") }],
          }),
        };
      }

      case EVENTS.COMPANY_STATUS_CHANGED: {
        return {
          title: translations.timeline.companyStatusChange({
            newStatus: getOntology({
              key: event.context?.new_status || -1,
              type: ONTOLOGIES.COMPANY_STATUS,
            }),
          }),
        };
      }

      case EVENTS.COMPANY_RESOURCE_ADDED: {
        return {
          title: translations.timeline.companyResourceAdded({
            resource:
              event.careprovider?.name ||
              event.careseeker?.name ||
              "unknown resource",
          }),
        };
      }

      case EVENTS.COMPANY_RESOURCE_REMOVED: {
        return {
          title: translations.timeline.companyResourceRemoved({
            resource:
              event.careprovider?.name ||
              event.careseeker?.name ||
              "unknown resource",
          }),
        };
      }

      case EVENTS.SOCIAL_WORKER_ASSIGNEE_CHANGED: {
        return {
          icon: UserIcon,
          title: translations.timeline.assigneeChanged({
            newAssigneeName,
            oldAssigneeName,
          }),
        };
      }

      case EVENTS.INSURANCE_ASSIGNEE_CHANGED: {
        return {
          icon: UserIcon,
          title: translations.timeline.assigneeChanged({
            newAssigneeName,
            oldAssigneeName,
          }),
        };
      }

      case EVENTS.PATIENT_MISSING_INFO: {
        return {
          message: event?.context?.message,
          title: getTranslationForAccount(
            translations.requestEvents.requestedPatientData,
            modularAccountName,
          ),
        };
      }

      case EVENTS.RECEIVER_MARKED_PREFERRED: {
        return {
          icon: StarIcon,
          message: null,
          title: getTranslationForAccount(
            translations.timeline.markedPatientPreferred,
            modularAccountName,
            providerName,
            {
              receiverTypeSingularPreferred: getOntology({
                key: event?.context?.patient_type,
                type: "receiverTypeSingularPreferred",
              }),
            },
          ),
        };
      }

      case EVENTS.RECEIVER_UNMARKED_PREFERRED: {
        return {
          icon: StarIcon,
          message: null,
          title: getTranslationForAccount(
            translations.timeline.removedPatientPreferred,
            modularAccountName,
            providerName,
            {
              receiverTypeSingularPreferred: getOntology({
                key: event?.context?.patient_type,
                type: "receiverTypeSingularPreferred",
              }),
            },
          ),
        };
      }

      case EVENTS.RECEIVER_EDITED_PREFERRED_REASON: {
        return {
          eventMessages: [getPatientWishReasonMessage(event, translations)],
          icon: StarIcon,
          title: getTranslationForAccount(
            translations.timeline.addedPatientWishReason,
            modularAccountName,
          ),
        };
      }

      case EVENTS.AHB_FORM_SUBMITTED_TO_INSURANCE: {
        if (!event.context?.ahb_form?.insurance_name) {
          console.error(
            "insurance_name not passed in event context ahb_form struct",
          );
        }
        return {
          icon: FileOutputIcon,
          title: translations.timeline.formSentToInsurance({
            accountName,
            insuranceName: event.context?.ahb_form?.insurance_name ?? "",
          }),
        };
      }
      case EVENTS.INSURANCE_REPLY_RECEIVED: {
        if (!event.context?.ahb_form?.insurance_name) {
          console.error(
            "insurance_name not passed in event context ahb_form struct",
          );
        }
        return {
          icon: FileInputIcon,
          title: translations.timeline.insuranceAnswered({
            insuranceName: event.context?.ahb_form?.insurance_name ?? "",
          }),
        };
      }
      case EVENTS.AHB_FORM_WITHDRAWN: {
        return {
          icon: BanIcon,
          title: translations.timeline.formWithdrawn({
            accountName,
            withdrawReason: translateWithdrawReason(
              event.context?.ahb_form?.withdraw_reason,
            ),
          }),
        };
      }
      case EVENTS.AHB_MEDICAL_FORM_APPROVAL_GRANTED: {
        return {
          icon: SignatureIcon,
          title:
            event.context?.ahb_form?.approved_by ===
            DTA_MEDICAL_FORM_APPROVED_BY.DOCTOR
              ? translations.insuranceApp.eligibilityTab.approvalFlow.historyFormApprovedDoctor(
                  { accountname: accountName },
                )
              : translations.insuranceApp.eligibilityTab.approvalFlow.historyFormApproved(
                  { accountname: accountName },
                ),
        };
      }
      case EVENTS.AHB_MEDICAL_FORM_APPROVAL_REVOKED: {
        return {
          icon: BanIcon,
          title:
            translations.insuranceApp.eligibilityTab.approvalFlow.historyFormApprovalRevoked(
              { accountname: accountName },
            ),
        };
      }
      case EVENTS.GRANT_ACCCESS_TO_REDACTED_FIELDS: {
        return {
          icon: LockIcon,
          title: translations.timeline.fullAccessPatientData({
            provider: providerName,
          }),
        };
      }
      case EVENTS.SSO_INTEGRATION_UPDATE: {
        const diff = event.context?.diff ?? [];
        const status = event.context?.new_status;
        if (diff.includes("active")) {
          if (status === SSO_STATUS_ACTIVE) {
            return {
              icon: LockIcon,
              title: translations.timeline.ssoActivated,
            };
          }
          if (status === SSO_STATUS_INACTIVE) {
            return {
              icon: LockIcon,
              title: translations.timeline.ssoDeactivated,
            };
          }
        }
        if (diff.includes("consent")) {
          return { icon: LockIcon, title: translations.timeline.ssoConsent };
        }

        return NO_EVENT;
      }

      case EVENTS.AUCTION_RESET:
      case EVENTS.CAREPROVIDER_STATUS_CHANGED:
      case EVENTS.CAREPROVIDER_ONBOARDING_STATUS_CHANGED:
      case EVENTS.CARESEEKER_STATUS_CHANGED:
      case EVENTS.ACCOUNT_PERMISSIONS:
      case EVENTS.PASSWORD_RESET_VERIFY:
      case EVENTS.REQUEST_COST_COVERAGE_UPDATED:
      case EVENTS.ACCOUNT_STATUS_CHANGED:
      case EVENTS.EMAIL_SENT:
      case EVENTS.WAITING_FOR_ELIGIBILITY:
      case EVENTS.FAX_ATTEMPTED:
      case EVENTS.SESSION_KEY_CREATED:
      case EVENTS.SESSION_KEY_REVOKED:
      case EVENTS.PENDING_SESSION_KEY_CREATED:
      case EVENTS.COST_PAYER_CHANGED:
      case EVENTS.SEALD_ENCRYPTION_CREATED:
      case EVENTS.BROWSER_NOTIFICATION_SENT:
      case EVENTS.CONSULTANT_CREATED:
      case EVENTS.CONSULTANT_UPDATED:
      case EVENTS.CONSULTANT_FILE_CREATED:
      case EVENTS.CONSULTANT_FILE_DELETED:
      case EVENTS.CONSULTANT_DELETED:
      case EVENTS.AHB_FORM_RETRY_FAILED:
      case EVENTS.AUCTION_ELECTIVE_FLOW: {
        return NO_EVENT;
      }

      case EVENTS.PREDICT_FEEDBACK_AGREE: {
        return {
          icon: ThumbsUpIcon,
          title: translations.timeline.predictFeedbackAgree({ accountName }),
        };
      }

      case EVENTS.PREDICT_FEEDBACK_DISAGREE: {
        return {
          icon: ThumbsDownIcon,
          title: translations.timeline.predictFeedbackDisagree({ accountName }),
        };
      }

      case EVENTS.PREDICT_RETRAIN: {
        return {
          icon: RefreshCwIcon,
          title: translations.timeline.predictRetrain,
        };
      }

      case EVENTS.PREDICT_RESET: {
        return {
          icon: RotateCcwIcon,
          title: translations.timeline.predictReset,
        };
      }

      case EVENTS.ACCOUNT_ROLE_STATION_UPDATE: {
        const stationNames = event.context?.new_stations?.join(", ") || "";
        return {
          icon: MapPinIcon,
          title: getTranslationForAccount(
            {
              myAccount:
                translations.timeline.stationAssignmentsUpdated.myAccount({
                  stationNames,
                }),
              otherAccount: ({ accountName }) =>
                translations.timeline.stationAssignmentsUpdated.otherAccount({
                  accountName,
                  stationNames,
                }),
            },
            modularAccountName,
          ),
        };
      }

      default: {
        // This is to guarantee that all events are covered
        const exhaustiveCheck: never = type;
        return exhaustiveCheck;
      }
    }
  }

  const specs = getSpecs(event.type as EventType);

  if (specs === NO_EVENT) {
    console.info(
      `Event type ${event.type} is returned as undefined purposefully`,
    );
    return undefined;
  }
  if (!specs?.title || specs.title === NO_TITLE) {
    console.groupCollapsed(`Event type ${event.type || ""} is not supported`);
    console.error(
      `Event type ${event.type || ""} is not supported ${safeLogEvent(event)}`,
    );
    console.groupEnd();
    return undefined;
  }

  if (specs.shouldHide?.includes(location)) {
    return undefined;
  }

  specs.sentByLabel = whoIsSender(accountId, event, translations);

  return specs;
}
